import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Paper,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import GenericDialog from "./Dialog";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DescriptionIcon from "@mui/icons-material/Description";

const renderFieldTitle = (label) => (
  <Typography
    variant='subtitle2'
    fontWeight={600}
    color='text.secondary'
    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
  >
    <EditNoteIcon fontSize='small' />
    {label}
  </Typography>
);

const loadTemplates = (storageKey) => {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === "string")
      : [];
  } catch {
    return [];
  }
};

const saveTemplates = (storageKey, values) => {
  if (!storageKey) return;
  localStorage.setItem(storageKey, JSON.stringify(values));
};

const reasonStorageKey = "admin-accounts-reasons";

const ReasonDialog = ({
  open,
  onClose,
  title,
  subtitle,
  onConfirm,
  loading = false,
  choice,
  fields = [],
  reasonFieldKey = "reason",
}) => {
  const [savedReasons, setSavedReasons] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSavedReasons(loadTemplates(reasonStorageKey));
  }, [open, reasonStorageKey]);

  const reasonField = useMemo(
    () => fields.find((f) => f.key === reasonFieldKey) || null,
    [fields, reasonFieldKey],
  );

  const persistReasons = useCallback(
    (next) => {
      setSavedReasons(next);
      saveTemplates(reasonStorageKey, next);
    },
    [reasonStorageKey],
  );

  const handleSaveCurrentReason = useCallback(() => {
    const value = (reasonField?.value || "").trim();
    if (!value) return;
    const next = [value, ...savedReasons.filter((r) => r !== value)].slice(
      0,
      20,
    );
    persistReasons(next);
  }, [reasonField?.value, savedReasons, persistReasons]);

  const handleApplySavedReason = useCallback(
    (value) => {
      if (!reasonField?.onChange) return;
      reasonField.onChange(value);
    },
    [reasonField],
  );

  const handleDeleteSavedReason = useCallback(
    (value) => {
      persistReasons(savedReasons.filter((r) => r !== value));
    },
    [savedReasons, persistReasons],
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      confirmText='Submit'
      onConfirm={onConfirm}
      loading={loading}
      content={
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {choice && (
            <Stack spacing={2}>
              <Typography
                variant='subtitle2'
                fontWeight={600}
                color='text.secondary'
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AccessTimeIcon fontSize='small' />
                {choice.label}
              </Typography>
              <ToggleButtonGroup
                color='primary'
                value={choice.value}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    choice.onChange(newValue);
                  }
                }}
                fullWidth
                sx={{ mb: 1 }} // 和下方的 TextField 保持一点间距
              >
                {choice.options.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          )}

          {fields &&
            fields.filter((f) => f.key !== reasonFieldKey).length > 0 && (
              <Stack spacing={2}>
                {fields
                  .filter((f) => f.key !== reasonFieldKey)
                  .map((field) => {
                    const {
                      key,
                      label,
                      value,
                      onChange,
                      placeholder,
                      type = "text", // 默认为 text
                      multiline = false,
                      minRows,
                      autoFocus = false,
                      helperText,
                      InputProps,
                      SelectProps,
                      options = [], // 用于 select 类型的选项数组
                    } = field;

                    // 1. 如果是 select 类型，渲染带下拉选项的 TextField
                    if (type === "select") {
                      return (
                        <Stack key={key} spacing={1}>
                          {renderFieldTitle(label)}
                          <TextField
                            select
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            fullWidth
                            variant='outlined'
                            autoFocus={autoFocus}
                            size='small' // 配合独立标题，输入框用 small 尺寸更精致
                            placeholder={placeholder}
                            helperText={helperText}
                            SelectProps={SelectProps}
                          >
                            {options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      );
                    }

                    if (type === "duration-days") {
                      // 计算具体的到期日：如果 value 是有效数字，就计算当前日期 + 天数，否则显示提示
                      const calculatedDate =
                        value && !isNaN(value)
                          ? dayjs()
                              .add(Number(value), "day")
                              .format("YYYY-MM-DD")
                          : "...";

                      return (
                        <Stack key={key} spacing={1.5}>
                          <Typography
                            variant='subtitle2'
                            fontWeight={600}
                            color='text.secondary'
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AccessTimeIcon fontSize='small' />
                            {label}
                          </Typography>

                          {/* 快捷天数按钮组 */}
                          <ToggleButtonGroup
                            color='primary'
                            value={value}
                            exclusive
                            onChange={(_, newValue) =>
                              newValue && onChange(newValue)
                            }
                            fullWidth
                            size='small'
                          >
                            <ToggleButton value={7}>7 Days</ToggleButton>
                            <ToggleButton value={30}>30 Days</ToggleButton>
                            <ToggleButton value={90}>90 Days</ToggleButton>
                            <ToggleButton value={365}>1 Year</ToggleButton>
                          </ToggleButtonGroup>

                          {/* 自定义天数输入框 */}
                          <TextField
                            size='small'
                            type='number'
                            fullWidth
                            placeholder={placeholder || "e.g. 60"}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position='end'>
                                  days
                                </InputAdornment>
                              ),
                            }}
                            helperText={
                              value && !isNaN(value)
                                ? `Expires on: ${calculatedDate}`
                                : "Enter number of days from today"
                            }
                          />
                        </Stack>
                      );
                    }

                    // 3. 默认情况：渲染普通的文本/密码/多行输入框
                    return (
                      <Stack key={key} spacing={1}>
                        {renderFieldTitle(label)}
                        <TextField
                          autoFocus={autoFocus}
                          type={type}
                          fullWidth
                          multiline={multiline}
                          minRows={minRows}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          placeholder={placeholder}
                          variant='outlined'
                          helperText={helperText}
                          InputProps={InputProps}
                          size='small' // 统一使用 small 尺寸
                        />
                      </Stack>
                    );
                  })}

                <Divider />
              </Stack>
            )}

          {reasonField && (
            <Box>
              {/* 加个带图标的小标题和分割线，明确分区 */}
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <DescriptionIcon fontSize='small' color='action' />
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  color='text.secondary'
                >
                  Reason & Notes
                </Typography>
              </Stack>

              {/* 原因的主输入框 */}
              <TextField
                fullWidth
                multiline
                minRows={2}
                label='Enter reason'
                value={reasonField.value}
                onChange={(e) => reasonField.onChange(e.target.value)}
                placeholder='Please explain the reason for this action...'
                variant='outlined'
                sx={{ mb: 1.5 }}
              />

              {/* 将保存和选择历史原因整合进一个带背景的面板 */}
              {/* <Paper
                variant='outlined'
                sx={{
                  p: 1.5,
                  bgcolor: "background.default",
                  borderRadius: 2,
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Button
                  size='small'
                  variant='contained' // 改用实心的 contained，视觉重点更突出
                  startIcon={<BookmarkAddIcon />}
                  onClick={handleSaveCurrentReason}
                  disabled={!String(reasonField.value || "").trim()}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1.5,
                    boxShadow: "none",
                  }}
                >
                  Save this reason as template
                </Button>

                <TextField
                  select
                  size='small'
                  label='Use saved template'
                  value=''
                  onChange={(e) => handleApplySavedReason(e.target.value)}
                  sx={{ minWidth: 200, flex: 1 }}
                >
                  <MenuItem value='' disabled>
                    Select a template
                  </MenuItem>
                  {savedReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 160,
                          }}
                        >
                          {reason}
                        </Typography>
                        <Tooltip title='Delete this template'>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={(evt) => {
                              evt.stopPropagation();
                              evt.preventDefault();
                              handleDeleteSavedReason(reason);
                            }}
                          >
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Paper> */}
            </Box>
          )}
        </Stack>
      }
    />
  );
};

export default ReasonDialog;
